import { useState, useEffect } from 'react';
import { NextLink } from 'components/NextLink';
import { CustomersList } from 'components/CustomersList';
import { BrandsList } from 'components/Brands';
import { CurrentUser, CustomerProfile, MetaFields, SimpleCustomer } from 'types';
import { getCustomerFollowing } from 'api';
import { formatNumberToLetteredValue } from 'utils/formatters';

import cx from 'classnames';
import styles from '../UserProfile.module.scss';

export type FollowingTabProps = {
  user?: CurrentUser;
  profile: CustomerProfile;
};

export const FollowingTab = ({ user, profile }: FollowingTabProps) => {
  const [followings, setFollowings] = useState<Array<SimpleCustomer>>([]);
  const [followingsMeta, setFollowingsMeta] = useState<MetaFields | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (profile.following_count) {
      fetchFollowings().then();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShowMore = () => {
    return fetchFollowings();
  };

  const fetchFollowings = async () => {
    if (isLoading || (followingsMeta && followingsMeta.current_page >= followingsMeta.last_page)) {
      return;
    }

    setIsLoading(true);

    const response = await getCustomerFollowing(profile.username, followingsMeta ? followingsMeta.current_page + 1 : 1);

    if (response.ok) {
      setFollowingsMeta(response.meta);
      setFollowings([...followings, ...response.data]);
    } else {
      setFollowings([]);
      setFollowingsMeta(undefined);
    }

    setIsLoading(false);
  };

  return (
    <div className={styles.tabContentWrapper}>
      <div className={cx(styles.content, styles.contentS)}>
        <div className={cx(styles.contentBlock)}>
          <div className={styles.contentBlockHeader}>
            <div className={styles.contentBlockHeaderTitle}>
              <span>Following Brands</span>
            </div>
            <NextLink href="/brands" className={cx(styles.contentViewAll, styles.tabShow)} title="View All" />
          </div>
          <div className={styles.contentBlockBody}>
            <BrandsList brands={profile.brands} type="halfed" />
            <br className={styles.tabHide} />
            <NextLink href="/brands" className={cx(styles.contentViewAll, styles.tabHide)} title="View All Brands" />
          </div>
        </div>
      </div>
      <div className={cx(styles.content, styles.contentM)}>
        <div className={styles.contentBlock}>
          <div className={cx(styles.contentBlockHeader, styles.followersHeader)}>
            <div className={styles.contentBlockHeaderTitle}>
              {formatNumberToLetteredValue(profile.following_count)} Following
            </div>
          </div>
          <div className={styles.contentBlockBody}>
            {followings.length > 0 && (
              <CustomersList
                currentUser={user}
                customers={followings}
                hasMorePages={!!followingsMeta && followingsMeta.current_page < followingsMeta.last_page}
                customWrapper
                onShowMore={handleShowMore}
                useDirectLinks={true}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
